import { registerLocaleData, DatePipe, CurrencyPipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeAr from '@angular/common/locales/ar';
import localeDa from '@angular/common/locales/da';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFi from '@angular/common/locales/fi';
import localeFr from '@angular/common/locales/fr';
import localeHr from '@angular/common/locales/hr';
import localeIt from '@angular/common/locales/it';
import localeJa from '@angular/common/locales/ja';
import localeNl from '@angular/common/locales/nl';
import localeNo from '@angular/common/locales/no';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
import localePtBr from '@angular/common/locales/pt';
import localeSe from '@angular/common/locales/se';
import localeSv from '@angular/common/locales/sv';
import localeSw from '@angular/common/locales/sw';
import {
  APP_INITIALIZER,
  ErrorHandler,
  Injector,
  LOCALE_ID,
  NgModule,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { TranslationService } from '@app/services/translation.service';
import { AuthInterceptor } from '@interceptors/auth.interceptor';
import { apiErrorInterceptor } from '@interceptors/api-error.interceptor';
import { GlobalErrorHandler } from '@shared/errors/global-error-handler';
import { WindowWithUser } from '@longnecktech/splash-commons-fe';
import { AppLocaleService } from '@services/app-locale.service';
import { SessionService } from '@services/session.service';
import { getSessionParamsFromUrl } from '@shared/utils/get-session-params.utils';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';

registerLocaleData(localeDe, 'de');
registerLocaleData(localeHr, 'hr');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeSe, 'se');
registerLocaleData(localePt, 'pt');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeSv, 'sv');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeJa, 'ja');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localeSw, 'sw');
registerLocaleData(localeAr, 'ar');
registerLocaleData(localePtBr, 'pt-BR');
registerLocaleData(localeDa, 'da');
registerLocaleData(localeNo, 'no');
registerLocaleData(localeIt, 'it');

export function startupServiceFactory(injector: Injector) {
  return () => {
    const sessionService = injector.get(SessionService);
    const appLocaleService = injector.get(AppLocaleService);

    const lang = (window as WindowWithUser)?.user?.language || 'en';

    appLocaleService.locale = TranslationService.supportedLanguages.includes(
      lang,
    )
      ? lang
      : 'en';

    sessionService.setSessionParams(getSessionParamsFromUrl());
  };
}

export function localeFactory(appLocaleService: AppLocaleService) {
  return appLocaleService.locale;
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [BrowserModule, BrowserAnimationsModule, AppRoutingModule],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: startupServiceFactory,
      deps: [Injector],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useFactory: localeFactory,
      deps: [AppLocaleService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [Sentry.TraceService],
      useFactory: () => () => {},
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    DatePipe,
    CurrencyPipe,
    provideHttpClient(
      withInterceptors([apiErrorInterceptor]),
      withInterceptorsFromDi(),
    ),
  ],
})
export class AppModule {}
